/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Capacitor } from '@capacitor/core';

/**
 * Detects if the current user agent is iOS (iPhone, iPad, iPod) or iPadOS.
 */
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIPadOS = ua.indexOf('Macintosh') > -1 && window.navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || (isIPadOS && !(window as any).MSStream);
};

/**
 * Detects if the current user agent is Android.
 */
export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android/.test(window.navigator.userAgent);
};

/**
 * Detects if the web application is running in standalone (PWA/HomeScreen) mode.
 */
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || !!(window.navigator as any).standalone || Capacitor.isNativePlatform();
};

/**
 * Detects if the app is running as a native app (Capacitor).
 */
export const isNative = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Detects if the current browser is Safari.
 */
export const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isChromeIOS = ua.indexOf('CriOS') > -1;
  const isSafariUA = ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1;
  return isSafariUA && !isChromeIOS;
};

/**
 * Detects if the app is running as the public web version
 * (public offline build accessed in a browser, not Capacitor native app).
 */
export const isPublicWeb = (): boolean => {
  if (typeof window !== 'undefined' && ((window as any).__FORCE_NATIVE_MODE__ || window.localStorage?.getItem('force_native_mode') === 'true')) {
    return false;
  }
  return import.meta.env.VITE_PUBLIC_OFFLINE === 'true' && !Capacitor.isNativePlatform();
};

/**
 * Detects if the app is running as a private build (not public offline build).
 */
export const isPrivateBuild = (): boolean => {
  return import.meta.env.VITE_PUBLIC_OFFLINE !== 'true';
};


/**
 * Scrolls the active input field on mobile devices so it rests at the top percentage (default 30%)
 * of the screen/viewport, preventing on-screen keyboards from obscuring the field.
 */
export const scrollInputIntoViewOnMobile = (
  inputElement: HTMLElement | null,
  topRatio: number = 0.30
) => {
  if (!inputElement || typeof window === 'undefined') return;
  const isMobile =
    window.innerWidth <= 768 ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0;

  if (!isMobile) return;

  const performScroll = () => {
    const visualViewport = window.visualViewport;
    const viewportHeight = visualViewport ? visualViewport.height : window.innerHeight;
    const viewportOffsetTop = visualViewport ? visualViewport.offsetTop : 0;
    
    // Target Y in the visible viewport (top 30%)
    const targetY = viewportOffsetTop + viewportHeight * topRatio;

    const scrollContainer = inputElement.closest('.overflow-y-auto') as HTMLElement | null;
    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = inputElement.getBoundingClientRect();
      
      // Absolute top of element relative to scrollContainer's scroll content
      const absoluteElementTop = scrollContainer.scrollTop + (elementRect.top - containerRect.top);
      const targetYInContainer = targetY - containerRect.top;
      const desiredScrollTop = Math.max(0, absoluteElementTop - targetYInContainer);

      if (typeof scrollContainer.scrollTo === 'function') {
        scrollContainer.scrollTo({ top: desiredScrollTop, behavior: 'smooth' });
      } else {
        scrollContainer.scrollTop = desiredScrollTop;
      }
    } else {
      const elementRect = inputElement.getBoundingClientRect();
      const absoluteElementTop = (window.scrollY || window.pageYOffset || 0) + elementRect.top;
      const desiredScrollTop = Math.max(0, absoluteElementTop - targetY);

      if (typeof window.scrollTo === 'function') {
        window.scrollTo({ top: desiredScrollTop, behavior: 'smooth' });
      } else {
        window.scrollY = desiredScrollTop;
      }
    }
  };

  performScroll();
  requestAnimationFrame(performScroll);
  setTimeout(performScroll, 60);
  setTimeout(performScroll, 150);
  setTimeout(performScroll, 300);
};

/**
 * Scrolls a form/element so it is perfectly centered vertically in the available
 * space between the fixed top header and the open mobile keyboard (visual viewport).
 */
export const scrollElementCenteredBetweenHeaderAndKeyboard = (
  targetElement: HTMLElement | null,
  headerSelector: string = 'header'
) => {
  if (!targetElement || typeof window === 'undefined') return;

  const isMobile =
    window.innerWidth <= 768 ||
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0;

  if (!isMobile) return;

  const performScroll = () => {
    const visualViewport = window.visualViewport;
    const viewportOffsetTop = visualViewport ? visualViewport.offsetTop : 0;
    const viewportHeight = visualViewport ? visualViewport.height : window.innerHeight;
    const keyboardTop = viewportOffsetTop + viewportHeight;

    const headerEl = document.querySelector(headerSelector) as HTMLElement | null;
    const headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;

    // Center position in visible viewport between header bottom and keyboard top
    const visibleCenterY = headerBottom + (keyboardTop - headerBottom) / 2;

    const targetRect = targetElement.getBoundingClientRect();
    const targetCenterY = targetRect.top + targetRect.height / 2;
    const deltaY = targetCenterY - visibleCenterY;

    const scrollContainer = targetElement.closest('.overflow-y-auto') as HTMLElement | null;
    if (scrollContainer) {
      const desiredScrollTop = Math.max(0, scrollContainer.scrollTop + deltaY);
      if (typeof scrollContainer.scrollTo === 'function') {
        scrollContainer.scrollTo({ top: desiredScrollTop, behavior: 'smooth' });
      } else {
        scrollContainer.scrollTop = desiredScrollTop;
      }
    } else {
      const desiredScrollTop = Math.max(0, (window.scrollY || window.pageYOffset || 0) + deltaY);
      if (typeof window.scrollTo === 'function') {
        window.scrollTo({ top: desiredScrollTop, behavior: 'smooth' });
      } else {
        window.scrollY = desiredScrollTop;
      }
    }
  };

  performScroll();
  requestAnimationFrame(performScroll);
  setTimeout(performScroll, 60);
  setTimeout(performScroll, 150);
  setTimeout(performScroll, 300);
};
